/**
 * Keeps the store's area-pricing tier in step with the ZIP, date and arrival.
 *
 * Mounted once, at the app root, because the question can change on any step —
 * the date on step 2, the ZIP when the address on step 3 turns out to be in a
 * different one. Each change first drops the tier, so the cart never shows a
 * saving that was quoted for some other day while the new answer is on its way.
 */

import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src";
import { setProximity } from "../actions";
import { percentForTier, tierForDay } from "../lib/day-prices";
import { arrivalFor, quoteFor, quoteKey } from "../lib/proximity";
import { peekAreaCalendar } from "./useAreaCalendar";

/**
 * When to ask again: a minute before the 30-minute token runs out, counted from
 * when the answer arrived. Not from the server's `expiresAt` against this
 * device's clock — a phone an hour fast read every token as already expired
 * and asked again at once, for ever.
 */
const REFRESH_AFTER_MS = 29 * 60_000;

export function useProximityQuote(): void {
  const dispatch = useDispatch();
  const state = useSelector((s: RootState) => s.bookingReducer);
  const key = quoteKey(state);
  const [refresh, setRefresh] = useState(0);
  const handled = useRef({ key: "", refresh: -1 });

  useEffect(() => {
    // Already answered for this question, and nothing asked to refresh it.
    if (key === state.proximityFor && handled.current.refresh === refresh) return;
    // A refresh of the same question keeps the current answer on screen, and
    // its token, until the new one lands.
    const refreshing = key === handled.current.key && key === state.proximityFor;
    handled.current = { key, refresh };

    if (!refreshing) {
      dispatch(setProximity({ tier: "none", percent: 0, token: null, key, pending: Boolean(key) }));
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    quoteFor(state)
      .then((quote) => {
        if (!quote || cancelled) return;
        dispatch(setProximity({ ...quote, pending: false }));
        /**
         * A tab left open outlives the token (30 minutes), and a stale tier
         * showed the old saving on every step until the Book click was
         * refused. Asked again just before it runs out.
         */
        if (quote.token) timer = setTimeout(() => setRefresh((n) => n + 1), REFRESH_AFTER_MS);
      })
      .catch(() => {
        if (cancelled) return;
        /**
         * The quote failed: price from the area calendar, the same data the
         * server resolves a tier from when no token arrives, so the cart shows
         * what checkout will charge. It used to drop to no tier while the date
         * step, reading the calendar, still said "You save".
         */
        const calendar = peekAreaCalendar(state.zip);
        const days = calendar?.status === "served" ? calendar.days : {};
        const tier = tierForDay(days[state.cleanDate], arrivalFor(state));
        dispatch(
          setProximity({
            tier,
            percent: percentForTier(calendar, tier),
            token: null,
            key,
            pending: false,
          })
        );
      });
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // Keyed on the question and the refresh alone: the state read here is the
    // state that made `key`, and this effect's own dispatch changes `proximityFor`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, refresh, dispatch]);
}
